import React, { useEffect, useState, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { Cookie, Check, X } from "lucide-react";

const STORAGE_KEY = "kc_cookie_consent";

function emitConsent(state) {
  try {
    window.kcConsent = state;
    window.dispatchEvent(new CustomEvent("kc:consent", { detail: state }));
  } catch {
    /* noop */
  }
}

export default function CookieBanner() {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);

  const persist = useCallback((state) => {
    try {
      localStorage.setItem(
        STORAGE_KEY,
        JSON.stringify({ ...state, updated_at: new Date().toISOString(), version: 1 })
      );
    } catch {
      /* noop */
    }
    emitConsent(state);
    setOpen(false);
  }, []);

  useEffect(() => {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      if (raw) {
        const parsed = JSON.parse(raw);
        emitConsent(parsed);
      } else {
        setOpen(true);
      }
    } catch {
      setOpen(true);
    }
  }, []);

  // Allow ESC to dismiss (= reject non-essential)
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => {
      if (e.key === "Escape") persist({ essential: true, marketing: false });
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, persist]);

  if (!open) return null;

  return (
    <div
      role="dialog"
      aria-live="polite"
      aria-label={t("cookie_banner.aria_label", "Cookie consent")}
      data-testid="cookie-banner"
      className="fixed bottom-4 left-4 right-4 sm:left-auto sm:right-6 sm:bottom-6 z-50 sm:max-w-md border border-primary/30 bg-card/95 backdrop-blur rounded-2xl shadow-2xl gold-glow"
    >
      <div className="p-5">
        <div className="flex items-start gap-3">
          <Cookie className="w-5 h-5 text-primary mt-0.5" />
          <div className="flex-1">
            <h2 className="font-display text-lg">{t("cookie_banner.title")}</h2>
            <p className="text-sm text-muted-foreground mt-2 leading-relaxed">
              {t("cookie_banner.body")}
            </p>
            <div className="mt-4 flex flex-wrap gap-2">
              <button
                type="button"
                onClick={() => persist({ essential: true, marketing: true })}
                className="inline-flex items-center gap-1.5 text-sm px-4 py-2 rounded-full bg-primary text-primary-foreground hover:bg-primary/90 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-amber-400"
                data-testid="cookie-accept-all"
              >
                <Check className="w-3.5 h-3.5" /> {t("cookie_banner.accept_all")}
              </button>
              <button
                type="button"
                onClick={() => persist({ essential: true, marketing: false })}
                className="inline-flex items-center gap-1.5 text-sm px-4 py-2 rounded-full border border-border text-muted-foreground hover:text-primary hover:border-primary/40 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-amber-400"
                data-testid="cookie-reject"
              >
                <X className="w-3.5 h-3.5" /> {t("cookie_banner.reject")}
              </button>
              <a
                href="/cookies"
                className="text-xs text-primary hover:underline self-center ml-1"
                data-testid="cookie-banner-policy-link"
              >
                {t("cookie_banner.read_more")}
              </a>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
